import React, {useContext, useEffect, useState} from "react";
import styled from "styled-components";
import {apiRequest, formatDate, formatFileSize, getAuthImage, toast} from "@/Helpers/general";
import {Button} from "primereact/button";
import ContentLoadingTemplate from "@/Components/Templates/ContentLoadingTemplate";
import {CLAIM_DOCUMENTS_ALLOWED_EXTENSIONS, CLAIM_DOCUMENTS_ROWS_NO} from "@/Config/constants";
import {AppContext} from "@/Components/Context/AppContext";
import FileUploadTemplate from "@/Components/Templates/Forms/FileUploadTemplate";
import ConfirmDialogTemplate from "@/Components/Templates/ConfirmDialogTemplate";
import InfoTooltipTemplate from "@/Components/Templates/InfoTooltipTemplate";
import DownloadButton from "../DownloadButton";
import {Img} from "react-image";
import {CaseContext} from "@/Components/Context/CaseContext";

const DocumentsWrap = styled.div`
    .documents-wrap {
        min-height: 35vh;
        position: relative;
    }
`;

/**
 * Component related to a claim's documents
 */
const Documents = ({caseId, caseData, disabled = false}) => {
    const {toastRef} = useContext(AppContext);
    const {getCaseData, ocrFile} = useContext(CaseContext);

    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [showDeleteFileDialog, setShowDeleteFileDialog] = useState();
    const [documentToBeDeleted, setDocumentToBeDeleted] = useState('');
    const [documentNameToBeDeleted, setDocumentNameToBeDeleted] = useState('');

    const [attachments, setAttachments] = useState([]);
    const [attachmentsLoading, setAttachmentsLoading] = useState(false);
    const [triggerClearAttachments, setTriggerClearAttachments] = useState();

    const [paymentPacks, setPaymentPacks] = useState([]);
    const [paymentPacksLoading, setPaymentPacksLoading] = useState(false);
    const [triggerClearPaymentPacks, setTriggerClearPaymentPacks] = useState();

    const [imgUrls, setImgUrls] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [showDuplicateDialogConfirm, setDuplicateDialogConfirm] = useState(false);


    /**
     * Add new attachments
     */
    const submitAttachmentsForm = async () => {
        if (attachments && attachments.length > 0) {
            setAttachmentsLoading(true);

            let formData = new FormData();

            attachments.forEach((file) => {
                formData.append('attachments[]', file);
            });

            apiRequest('post', 'case/' + caseId + '/documents', {
                data: formData,
                onSuccess: (response) => {
                    setAttachmentsLoading(false);

                    // clear data
                    setAttachments([]);
                    setImgUrls([]);
                    setDocuments([]);
                    setTriggerClearAttachments(Date.now());

                    // refresh documents list
                    loadDocuments();

                    toast(toastRef, 'success', 'The files have been saved.');
                },
                onFailure: (error) => {
                    setAttachmentsLoading(false);

                    if (error.status == 422) {
                        if (error.messages) {
                            let errorMessages = error.messages,
                                newFormErrors = {};

                            for (let key in errorMessages) {
                                if (Object.hasOwnProperty.call(errorMessages, key)) {
                                    newFormErrors[key] = errorMessages[key];
                                }
                            }

                            toast(toastRef, 'error', 'Error saving files.');

                        } else {
                            toast(toastRef, 'error', 'Error saving files.');
                        }

                    } else if (error.status == 403) {
                        toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                    } else {
                        toast(toastRef, 'error', 'Error saving files.');
                    }
                }
            });

        }
    };

    /**
     * Add new payment packs
     */
    const submitPaymentPacksForm = async (force) => {
        if (paymentPacks) {
            setPaymentPacksLoading(true);

            let formData = new FormData();

            if (force) {
                formData.append('force', true);
            }

            formData.append('mainFile', paymentPacks);

            apiRequest('post', 'case/' + caseId + '/upload-ocr', {
                data: formData,
                onSuccess: (response) => {
                    setPaymentPacksLoading(false);

                    // refresh case data
                    getCaseData({switchToTab: 'ocr'});

                    // clear data
                    setPaymentPacks([]);
                    setImgUrls([]);
                    setDocuments([]);
                    setTriggerClearPaymentPacks(Date.now());

                    // refresh documents list
                    loadDocuments();

                    toast(toastRef, 'success', 'The payment packs have been saved.');
                },
                onFailure: (error) => {
                    setPaymentPacksLoading(false);

                    // for local testing
                    // if (error.status != 403) {
                    //     getCaseData({ switchToTab: 'ocr' });
                    // }

                    if (error.status == 422) {
                        if (error.messages) {
                            let errorMessages = error.messages,
                                newFormErrors = {};

                            for (let key in errorMessages) {
                                if (Object.hasOwnProperty.call(errorMessages, key)) {
                                    newFormErrors[key] = errorMessages[key];
                                }
                            }

                            toast(toastRef, 'error', 'Error saving payment packs.');

                        } else {
                            toast(toastRef, 'error', 'Error saving payment packs.');
                        }

                    } else if (error.status == 403) {
                        console.log(error)
                        if (error.messages) {
                            if (error.messages == 'This file was already uploaded!') {
                                setDuplicateDialogConfirm(true);
                            } else if (error.messages.message) {
                                toast(toastRef, 'error', error.messages.message);
                            } else if (error.messages.mainFile) {
                                for (let key in error.messages.mainFile) {
                                    toast(toastRef, 'error', error.messages.mainFile[key]);
                                }
                            } else {
                                toast(toastRef, 'error', error.messages);
                            }
                        } else {
                            toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');
                        }
                    } else {
                        toast(toastRef, 'error', 'Error saving payment packs.');
                    }
                }
            });

        }
    };


    /**
     * Add files in the state
     *
     * @param {object} event data related to the files
     */
    const uploadHandler = (event, type) => {
        if (type === "attachments") {
            setAttachments(event.files);
        } else if (type === "payment-pack") {
            setPaymentPacks(event.files[0])
        }
    };

    /**
     * Remove a file from the attachments
     *
     * @param {object} event data related to the file
     */
    const onAttachmentRemove = (event) => {
        let copyAttachments = [...attachments];

        if (event?.file?.name) {
            copyAttachments = copyAttachments.filter((file) => file.name != event.file.name);
        }

        setAttachments(copyAttachments);
    };

    /**
     * Load images and save their url in the state
     *
     * @param {object} docs list of documents
     */
    const loadImages = async (docs) => {
        let imagesUrls = [],
            sortedDocs = [];

        await Promise.all(docs.map(async (doc) => {
            if (doc.file_mime) {
                if (doc.file_mime.search('image') !== -1) {
                    let imgUrl = await getAuthImage(doc.view_url);

                    if (imgUrl) {
                        imagesUrls.push({
                            id: doc.id,
                            url: imgUrl
                        })
                    }
                }
            }
        }));
        setImgUrls(imagesUrls);

        setDocuments(sortedDocs.length > 0 ? sortedDocs : docs);
        setDocumentsLoading(false);
    };

    /**
     * Get documents from the API
     */
    const loadDocuments = async () => {
        setDocumentsLoading(true);

        let requestParams = {
            page: currentPage,
            limit: CLAIM_DOCUMENTS_ROWS_NO
        };

        apiRequest('get', 'case/' + caseId + '/documents', {
            data: requestParams,
            onSuccess: (response) => {
                if (response.result && response.result.length > 0) {
                    loadImages(response.result);

                } else {
                    setDocumentsLoading(false);
                }
            },
            onFailure: () => {
                setDocumentsLoading(false);
            }
        });
    };

    /**
     * Generate a preview of a file
     *
     * @param {object} doc data related to the file
     */
    const filePreview = (doc, uploadPreview = false) => {
        let preview = "";
        let type = uploadPreview ? doc?.type : doc?.file_mime;

        switch (type) {
            case "application/pdf":
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file-pdf"></i>
                    </div>
                );
                break;
            case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file-word"></i>
                    </div>
                );
                break;
            case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file-excel"></i>
                    </div>
                );
                break;
            case "application/msword":
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file-word"></i>
                    </div>
                );
                break;
            case "application/vnd.ms-excel":
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file-excel"></i>
                    </div>
                );
                break;
            default:
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file"></i>
                    </div>
                );
                break;
        }

        if (type.search("image") !== -1) {
            let getImg = uploadPreview ? doc.objectURL : imgUrls.find((img) => img.id == doc.id);

            if (getImg) {
                preview = (
                    <Img
                        src={getImg.url}
                        alt=""
                        unloader={
                            <div className="no-picture">
                                <i className="pi pi-image"></i>
                            </div>
                        }
                    />
                );
            }
        }

        return preview;
    };

    /**
     * Delete a document
     */
    const deleteDocument = () => {
        if (documentToBeDeleted) {
            apiRequest('delete', 'case/' + caseId + '/document/' + documentToBeDeleted, {
                onSuccess: (response) => {
                    if (response?.data?.responseType == 'success') {
                        setDocumentToBeDeleted();
                        setDocumentNameToBeDeleted('');
                        setShowDeleteFileDialog(false);

                        setDocuments([]);
                        loadDocuments();

                    } else {
                        toast(toastRef, 'error', 'Error deleting document.');
                    }
                },
                onFailure: (error) => {
                    if (error.status == 403) {
                        toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                    } else {
                        toast(toastRef, 'error', 'Error deleting document.');
                    }
                }
            });
        }
    };

    /**
     * The preview (for files other than images) which appears after you select a file
     */
    const itemTemplate = (file, event) => {
        return (
            <div className="p-fileupload-row documents-files-format">
                <div className="file-extension-icon">{filePreview(file, true)}</div>

                <div className="p-fileupload-filename">{file.name}</div>

                <div>{formatFileSize(file.size)}</div>

                <div>
                    <button
                        type="button"
                        className="p-button p-component p-button-icon-only"
                        onClick={() => {
                            event.onRemove();
                        }}
                    >
                        <span className="p-button-icon p-c pi pi-times"></span>
                        <span className="p-button-label p-c">&nbsp;</span>
                    </button>
                </div>
            </div>
        );
    };

    // init
    useEffect(() => {
        loadDocuments();
    }, []);

    return (
        <DocumentsWrap>
            <div className="custom-content">
                <div className="documents-wrap">
                    <table className="documents-list simple-table">
                        <tbody>
                        {documents.map((doc) => {
                            return (
                                <tr key={doc.id}>
                                    <td className="file-preview">{filePreview(doc)}</td>

                                    <td className="file-name">{doc.name}</td>
                                    <td className="">{formatFileSize(doc.size)}</td>
                                    <td className="">{formatDate(doc.created_at)}</td>
                                    {(doc.attachment_type !== 'payment_pack') ? (
                                        <td className="file-actions flex align-items-center">
                                            <InfoTooltipTemplate target="delete-file-info" message="This file can not be deleted." className="ml-2 large"/>
                                            <DownloadButton doc={doc} margin={4}/>
                                        </td>
                                    ) : disabled ? (
                                        <td className="file-actions flex justify-content-center">
                                            <DownloadButton doc={doc}/>
                                        </td>
                                    ) : (
                                        <td className="file-actions">
                                            <Button
                                                className="p-button-icon-only"
                                                onClick={() => {
                                                    setDocumentToBeDeleted(doc.id);
                                                    setDocumentNameToBeDeleted(doc.name);
                                                    setShowDeleteFileDialog(true);
                                                }}
                                                icon="pi pi-times"
                                            />
                                            <DownloadButton doc={doc} margin={3}/>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>

                    {documentsLoading && <ContentLoadingTemplate/>}
                </div>
                <div className="file-upload-forms">
                    <div className={'files-upload dacb-upload'}>
                        {!disabled && (
                            <>
                                <div className="grid">
                                    <div className="col-12">
                                        <label htmlFor="" className="block mb-3 flex">
                                            <span>Add new files: </span>
                                            <InfoTooltipTemplate target="add-new-files-info"
                                                                 message="Add all claim supportive documentation here: Medical report, Vehicle damage report, engineer's report etc"
                                                                 className="ml-2 large"/>
                                        </label>
                                        <FileUploadTemplate
                                            itemTemplate={itemTemplate}
                                            uploadHandler={(e) => uploadHandler(e, 'attachments')}
                                            accept={CLAIM_DOCUMENTS_ALLOWED_EXTENSIONS.join(',')}
                                            onFileRemove={onAttachmentRemove}
                                            triggerClear={triggerClearAttachments}
                                        />
                                    </div>
                                </div>
                                <hr/>

                                <div className="text-right">
                                    <Button
                                        label="Save new files"
                                        onClick={submitAttachmentsForm}
                                        loading={attachmentsLoading}
                                        loadingIcon="pi pi-spin pi-sun"
                                        iconPos="center"
                                    />
                                </div>
                            </>
                        )}
                    </div>


                    <div className="payment-packs-upload">
                        {!disabled && (
                            <>
                                <div className="grid">
                                    <div className="col-12">
                                        <label htmlFor="" className="block mb-3 flex">
                                            <span>Add new Payment Pack: </span>
                                            <InfoTooltipTemplate target="add-new-pp-info" message="Add ONLY Payment Pack here" className="ml-2 large"/>
                                        </label>
                                        <FileUploadTemplate
                                            itemTemplate={itemTemplate}
                                            uploadHandler={(e) => uploadHandler(e, 'payment-pack')}
                                            accept={'application/pdf'}
                                            multiple={false}
                                            onFileRemove={() => {
                                                setPaymentPacks();
                                            }}
                                            triggerClear={triggerClearPaymentPacks}
                                        />
                                    </div>
                                </div>
                                <hr/>

                                <div className="text-right">
                                    {
                                        ocrFile?.is_processing &&
                                        <InfoTooltipTemplate target="add-new-pp-button-info" message="OCR file is currently in processing" className="mr-2 large"/>
                                    }

                                    <Button
                                        label="Save payment pack"
                                        onClick={() => {
                                            submitPaymentPacksForm(false)
                                        }}
                                        loading={paymentPacksLoading}
                                        loadingIcon="pi pi-spin pi-sun"
                                        iconPos="center"
                                        disabled={ocrFile?.is_processing}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialogTemplate
                title="Duplicate file"
                message={
                    'The file you selected has already been uploaded. Do you want to continue processing this document even if it is a duplicate?'
                }
                onConfirm={() => {
                }}
                visible={showDuplicateDialogConfirm}
                setVisible={setDuplicateDialogConfirm}
                footer={
                    <>
                        <Button
                            label="Continue processing"
                            className="p-button-outlined mr-3"
                            onClick={() => {
                                submitPaymentPacksForm(true);
                                setDuplicateDialogConfirm(false);
                            }}
                            loading={paymentPacksLoading}
                            loadingIcon="pi pi-spin pi-sun"
                            iconPos="center"
                        />
                        <Button
                            label="Cancel"
                            className="p-button-outlined p-button-secondary"
                            onClick={() => {
                                setDuplicateDialogConfirm(false);
                            }}
                        />
                    </>
                }
            />
            <ConfirmDialogTemplate
                title="Delete document"
                message={'Are you sure you wish to delete the document named "' + documentNameToBeDeleted + '"?'}
                onConfirm={deleteDocument}
                visible={showDeleteFileDialog}
                setVisible={setShowDeleteFileDialog}
            />
        </DocumentsWrap>
    );
};

export default Documents;
